import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type {
  Customer,
  QuoteRequest,
  Quote,
  Appointment,
  Team,
  Job,
  RequestStatus,
  QuoteStatus,
  JobStatus,
  VideoStatus,
  Activity,
  Payment,
  Expense,
  AppNotification,
} from '@/types';
import { useAuth } from '@/lib/auth';
import { SERVICES, REGIONS, EXPENSE_CATEGORIES } from '@/mocks/constants';
import {
  ensureConfigSeed,
  loadCustomers,
  loadRequests,
  loadQuotes,
  loadAppointments,
  loadTeams,
  loadJobs,
  loadPayments,
  loadExpenses,
  loadNotifications,
  persistCustomer,
  persistRequest,
  persistRequestActivity,
  persistQuote,
  persistAppointment,
  persistJob,
  persistJobActivity,
  persistJobNote,
  persistPayment,
  persistExpense,
  persistNotification,
  persistNotificationRead,
  persistTeam,
} from '@/lib/db';

const CONFIG_TEAMS: Team[] = [
  { id: 'T-01', name: 'A Ekibi', members: [], status: 'Müsait', currentJob: '', location: '', nextJobTime: '' },
  { id: 'T-02', name: 'B Ekibi', members: [], status: 'Müsait', currentJob: '', location: '', nextJobTime: '' },
  { id: 'T-03', name: 'C Ekibi', members: [], status: 'Müsait', currentJob: '', location: '', nextJobTime: '' },
];

interface AppState {
  customers: Customer[];
  requests: QuoteRequest[];
  quotes: Quote[];
  appointments: Appointment[];
  teams: Team[];
  jobs: Job[];
  payments: Payment[];
  expenses: Expense[];
  notifications: AppNotification[];
  loading: boolean;
  error: string | null;
}

interface AppStoreValue extends AppState {
  reload: () => void;
  setRequestStatus: (id: string, status: RequestStatus) => void;
  addRequestActivity: (id: string, activity: Activity) => void;
  setRequestVideoStatus: (id: string, videoStatus: VideoStatus) => void;
  addRequest: (request: QuoteRequest) => void;
  setQuoteStatus: (id: string, status: QuoteStatus) => void;
  setJobStatus: (id: string, status: JobStatus) => void;
  addJobNote: (id: string, note: string) => void;
  addJobTimeline: (id: string, activity: Activity) => void;
  addAppointment: (appointment: Appointment) => void;
  updateAppointment: (id: string, patch: Partial<Appointment>) => void;
  addQuote: (quote: Quote) => void;
  addCustomer: (customer: Customer) => void;
  addPayment: (payment: Payment) => void;
  addExpense: (expense: Expense) => void;
  addNotification: (notification: AppNotification) => void;
  markNotificationRead: (id: string) => void;
  markAllNotificationsRead: () => void;
  setTeamStatus: (id: string, status: string) => void;
  assignTeam: (id: string, currentJob: string, location: string) => void;
}

const AppStoreContext = createContext<AppStoreValue | null>(null);

function timestamp(): string {
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

function swallowError(fn: () => Promise<void>) {
  fn().catch((err) => {
    // Surface write failures without crashing the UI.
    console.error('[VİAYPİİ] Kaydetme hatası:', err);
  });
}

export function AppStoreProvider({ children }: { children: ReactNode }) {
  const { authorized } = useAuth();

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [requests, setRequests] = useState<QuoteRequest[]>([]);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!authorized) {
      setCustomers([]);
      setRequests([]);
      setQuotes([]);
      setAppointments([]);
      setTeams([]);
      setJobs([]);
      setPayments([]);
      setExpenses([]);
      setNotifications([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        await ensureConfigSeed({
          services: SERVICES,
          regions: REGIONS,
          expenseCategories: EXPENSE_CATEGORIES,
          teams: CONFIG_TEAMS,
        });
        const [c, r, q, a, t, j, p, e, n] = await Promise.all([
          loadCustomers(),
          loadRequests(),
          loadQuotes(),
          loadAppointments(),
          loadTeams(),
          loadJobs(),
          loadPayments(),
          loadExpenses(),
          loadNotifications(),
        ]);
        if (cancelled) return;
        setCustomers(c);
        setRequests(r);
        setQuotes(q);
        setAppointments(a);
        setTeams(t);
        setJobs(j);
        setPayments(p);
        setExpenses(e);
        setNotifications(n);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Veriler yüklenirken bir hata oluştu.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorized, reloadKey]);

  const reload = () => setReloadKey((k) => k + 1);

  const setRequestStatus = (id: string, status: RequestStatus) => {
    setRequests((prev) => {
      const next = prev.map((r) =>
        r.id === id
          ? {
              ...r,
              status,
              lastContact: 'Şimdi',
              activities: [...r.activities, { time: timestamp(), text: `Durum: ${status}` }],
            }
          : r,
      );
      const updated = next.find((r) => r.id === id);
      if (updated) {
        swallowError(async () => {
          await persistRequest(updated);
          await persistRequestActivity(id, { time: timestamp(), text: `Durum: ${status}` });
        });
      }
      return next;
    });
  };

  const addRequestActivity = (id: string, activity: Activity) => {
    setRequests((prev) =>
      prev.map((r) => (r.id === id ? { ...r, activities: [...r.activities, activity] } : r)),
    );
    swallowError(async () => {
      await persistRequestActivity(id, activity);
    });
  };

  const setRequestVideoStatus = (id: string, videoStatus: VideoStatus) => {
    setRequests((prev) => {
      const next = prev.map((r) =>
        r.id === id
          ? {
              ...r,
              videoStatus,
              status: videoStatus === 'Video Geldi' ? 'Video Geldi' : r.status,
              lastContact: 'Şimdi',
              activities: [...r.activities, { time: timestamp(), text: `Video durumu: ${videoStatus}` }],
            }
          : r,
      );
      const updated = next.find((r) => r.id === id);
      if (updated) {
        swallowError(async () => {
          await persistRequest(updated);
          await persistRequestActivity(id, { time: timestamp(), text: `Video durumu: ${videoStatus}` });
        });
      }
      return next;
    });
  };

  const addRequest = (request: QuoteRequest) => {
    setRequests((prev) => [request, ...prev]);
    swallowError(async () => {
      await persistRequest(request);
      for (const a of request.activities) {
        await persistRequestActivity(request.id, a);
      }
    });
  };

  const setQuoteStatus = (id: string, status: QuoteStatus) => {
    setQuotes((prev) => {
      const next = prev.map((q) => (q.id === id ? { ...q, status } : q));
      const updated = next.find((q) => q.id === id);
      if (updated) swallowError(async () => persistQuote(updated));
      return next;
    });
  };

  const setJobStatus = (id: string, status: JobStatus) => {
    setJobs((prev) => {
      const next = prev.map((j) =>
        j.id === id
          ? { ...j, status, timeline: [...j.timeline, { time: timestamp(), text: status }] }
          : j,
      );
      const updated = next.find((j) => j.id === id);
      if (updated) {
        swallowError(async () => {
          await persistJob(updated);
          await persistJobActivity(id, { time: timestamp(), text: status });
        });
      }
      return next;
    });
  };

  const addJobNote = (id: string, note: string) => {
    setJobs((prev) => prev.map((j) => (j.id === id ? { ...j, notes: [...j.notes, note] } : j)));
    swallowError(async () => persistJobNote(id, note));
  };

  const addJobTimeline = (id: string, activity: Activity) => {
    setJobs((prev) =>
      prev.map((j) => (j.id === id ? { ...j, timeline: [...j.timeline, activity] } : j)),
    );
    swallowError(async () => persistJobActivity(id, activity));
  };

  const addAppointment = (appointment: Appointment) => {
    setAppointments((prev) => [...prev, appointment]);
    swallowError(async () => persistAppointment(appointment));
  };

  const updateAppointment = (id: string, patch: Partial<Appointment>) => {
    setAppointments((prev) => {
      const next = prev.map((a) => (a.id === id ? { ...a, ...patch } : a));
      const updated = next.find((a) => a.id === id);
      if (updated) swallowError(async () => persistAppointment(updated));
      return next;
    });
  };

  const addQuote = (quote: Quote) => {
    setQuotes((prev) => [...prev, quote]);
    swallowError(async () => persistQuote(quote));
  };

  const addCustomer = (customer: Customer) => {
    setCustomers((prev) => [...prev, customer]);
    swallowError(async () => persistCustomer(customer));
  };

  const addPayment = (payment: Payment) => {
    setPayments((prev) => [payment, ...prev]);
    swallowError(async () => persistPayment(payment));
  };

  const addExpense = (expense: Expense) => {
    setExpenses((prev) => [expense, ...prev]);
    swallowError(async () => persistExpense(expense));
  };

  const addNotification = (notification: AppNotification) => {
    setNotifications((prev) => [notification, ...prev]);
    swallowError(async () => persistNotification(notification));
  };

  const markNotificationRead = (id: string) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    swallowError(async () => persistNotificationRead(id, true));
  };

  const markAllNotificationsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    swallowError(async () => {
      for (const n of notifications) {
        if (!n.read) await persistNotificationRead(n.id, true);
      }
    });
  };

  const setTeamStatus = (id: string, status: string) => {
    setTeams((prev) => {
      const next = prev.map((t) => (t.id === id ? { ...t, status } : t));
      const updated = next.find((t) => t.id === id);
      if (updated) swallowError(async () => persistTeam(updated));
      return next;
    });
  };

  const assignTeam = (id: string, currentJob: string, location: string) => {
    setTeams((prev) => {
      const next = prev.map((t) =>
        t.id === id ? { ...t, status: 'Aktif İşte', currentJob, location } : t,
      );
      const updated = next.find((t) => t.id === id);
      if (updated) swallowError(async () => persistTeam(updated));
      return next;
    });
  };

  const value: AppStoreValue = {
    customers,
    requests,
    quotes,
    appointments,
    teams,
    jobs,
    payments,
    expenses,
    notifications,
    loading,
    error,
    reload,
    setRequestStatus,
    addRequestActivity,
    setRequestVideoStatus,
    addRequest,
    setQuoteStatus,
    setJobStatus,
    addJobNote,
    addJobTimeline,
    addAppointment,
    updateAppointment,
    addQuote,
    addCustomer,
    addPayment,
    addExpense,
    addNotification,
    markNotificationRead,
    markAllNotificationsRead,
    setTeamStatus,
    assignTeam,
  };

  return <AppStoreContext.Provider value={value}>{children}</AppStoreContext.Provider>;
}

export function useAppStore(): AppStoreValue {
  const ctx = useContext(AppStoreContext);
  if (!ctx) {
    throw new Error('useAppStore must be used within AppStoreProvider');
  }
  return ctx;
}